'use client';

import * as React from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

/* ═══════════════════════════════════════════════════
   Toggle — a pressable button with persistent on/off state
   ToggleGroup — compound container (single / multiple select)
   APG: Button (aria-pressed) + Toolbar (roving tabindex)
   ═══════════════════════════════════════════════════ */

export type ToggleSize = 'sm' | 'md';

export interface ToggleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>, UnsafeProps {
  /** Controlled pressed state. */
  pressed: boolean;
  /** Called when the user toggles the button. */
  onPressedChange: (pressed: boolean) => void;
  /** Visual size. */
  size?: ToggleSize;
  /** Additional class name. */
  className?: string;
  children: ReactNode;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  {
    pressed,
    onPressedChange,
    size = 'md',
    disabled,
    className,
    unsafe_className,
    unsafe_style,
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(e);
    onPressedChange(!pressed);
  };

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      className={cx(
        'j-toggle',
        size === 'sm' ? 'j-toggle-sm' : 'j-toggle-md',
        pressed && 'j-toggle-pressed',
        className,
        unsafe_className,
      )}
      style={unsafe_style}
      onClick={handleClick}
    >
      {children}
    </button>
  );
});

/* ── ToggleGroup ─────────────────────────────────── */

interface ToggleGroupContextValue {
  type: 'single' | 'multiple';
  value: string | string[];
  onItemToggle: (itemValue: string) => void;
  onFocusItem: (itemValue: string) => void;
  size: ToggleSize;
  disabled: boolean;
  focusedValue: string | null;
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

interface ToggleGroupSingleProps extends UnsafeProps {
  type: 'single';
  value: string;
  onValueChange: (value: string) => void;
  size?: ToggleSize;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  /** Required accessible name for the toolbar. */
  ariaLabel: string;
}

interface ToggleGroupMultipleProps extends UnsafeProps {
  type: 'multiple';
  value: string[];
  onValueChange: (value: string[]) => void;
  size?: ToggleSize;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  /** Required accessible name for the toolbar. */
  ariaLabel: string;
}

export type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps;

interface ToggleGroupItemProps extends UnsafeProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

function ToggleGroupItem({
  value,
  disabled: itemDisabled,
  className,
  unsafe_className,
  unsafe_style,
  children,
}: ToggleGroupItemProps) {
  const ctx = React.useContext(ToggleGroupContext);
  if (!ctx) {
    throw new Error('<ToggleGroup.Item> must be used within a <ToggleGroup>.');
  }

  const {
    type,
    value: groupValue,
    onItemToggle,
    onFocusItem,
    size,
    disabled: groupDisabled,
    focusedValue,
  } = ctx;
  const btnRef = React.useRef<HTMLButtonElement>(null);

  const isPressed =
    type === 'single' ? groupValue === value : (groupValue as string[]).includes(value);
  const isDisabled = groupDisabled || !!itemDisabled;
  const isFocused = focusedValue === value;

  React.useEffect(() => {
    if (isFocused && btnRef.current) {
      btnRef.current.focus();
    }
  }, [isFocused]);

  return (
    <button
      ref={btnRef}
      type="button"
      data-value={value}
      aria-pressed={isPressed}
      disabled={isDisabled}
      tabIndex={isFocused ? 0 : -1}
      className={cx(
        'j-toggle',
        size === 'sm' ? 'j-toggle-sm' : 'j-toggle-md',
        isPressed && 'j-toggle-pressed',
        className,
        unsafe_className,
      )}
      style={unsafe_style}
      onClick={() => {
        if (!isDisabled) {
          onFocusItem(value);
          onItemToggle(value);
        }
      }}
    >
      {children}
    </button>
  );
}

function ToggleGroupRoot(props: ToggleGroupProps) {
  const {
    type,
    value,
    onValueChange,
    size = 'md',
    orientation = 'horizontal',
    disabled = false,
    className,
    unsafe_className,
    unsafe_style,
    children,
    ariaLabel,
  } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Initial focused value: the selected item (single) or first item (multiple).
  // Falls back to null — first enabled item gets tabIndex=0 via getButtons().
  const [focusedValue, setFocusedValue] = React.useState<string | null>(() => {
    if (type === 'single' && value) return value;
    return null;
  });

  const onItemToggle = React.useCallback(
    (itemValue: string) => {
      if (type === 'single') {
        (onValueChange as (v: string) => void)(itemValue);
      } else {
        const current = value as string[];
        const next = current.includes(itemValue)
          ? current.filter((v) => v !== itemValue)
          : [...current, itemValue];
        (onValueChange as (v: string[]) => void)(next);
      }
    },
    [type, value, onValueChange],
  );

  const onFocusItem = React.useCallback((itemValue: string) => {
    setFocusedValue(itemValue);
  }, []);

  /** Get all toggle buttons in DOM order from the container. */
  const getButtons = React.useCallback((): HTMLButtonElement[] => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll<HTMLButtonElement>('button.j-toggle'));
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const isHorizontal = orientation === 'horizontal';
      const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';
      const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';

      if (![nextKey, prevKey, 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();

      const buttons = getButtons();
      const enabled = buttons.filter((b) => !b.disabled);
      if (enabled.length === 0) return;

      // Find which enabled button is currently focused
      const currentBtn = buttons.find((b) => b.dataset.value === focusedValue);
      const currentIdx = currentBtn ? enabled.indexOf(currentBtn) : -1;

      let nextBtn: HTMLButtonElement;
      switch (e.key) {
        case nextKey:
          nextBtn =
            currentIdx === -1 || currentIdx >= enabled.length - 1
              ? enabled[0]
              : enabled[currentIdx + 1];
          break;
        case prevKey:
          nextBtn = currentIdx <= 0 ? enabled[enabled.length - 1] : enabled[currentIdx - 1];
          break;
        case 'Home':
          nextBtn = enabled[0];
          break;
        case 'End':
          nextBtn = enabled[enabled.length - 1];
          break;
        default:
          return;
      }

      setFocusedValue(nextBtn.dataset.value ?? null);
    },
    [orientation, focusedValue, getButtons],
  );

  // If focusedValue doesn't match any current button (e.g., item removed),
  // reset to the first enabled button's value.
  React.useEffect(() => {
    const buttons = getButtons();
    const hasFocused = buttons.some((b) => b.dataset.value === focusedValue && !b.disabled);
    if (!hasFocused && buttons.length > 0) {
      const firstEnabled = buttons.find((b) => !b.disabled);
      if (firstEnabled) setFocusedValue(firstEnabled.dataset.value ?? null);
    }
  }, [focusedValue, getButtons, children]);

  const ctxValue = React.useMemo<ToggleGroupContextValue>(
    () => ({
      type,
      value,
      onItemToggle,
      onFocusItem,
      size,
      disabled,
      focusedValue,
    }),
    [type, value, onItemToggle, onFocusItem, size, disabled, focusedValue],
  );

  return (
    <ToggleGroupContext.Provider value={ctxValue}>
      <div
        ref={containerRef}
        role="toolbar"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        className={cx(
          'j-toggle-group',
          orientation === 'vertical' && 'j-toggle-group-vertical',
          className,
          unsafe_className,
        )}
        style={unsafe_style}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

export const ToggleGroup = Object.assign(ToggleGroupRoot, {
  Item: ToggleGroupItem,
});
